/**
 * @Email : sgarcia@ibl.mx
 * @Author: Susana Garcia Rodriguez
 * @jobTittle: Frontend
 */

import { URL } from '../../../../../Apis/Constants';
import {
    DATE_TIME_FORMAT_NAME_MODEL,
    downloadActionFile,
    EMPTY_VALUES,
    getDateFormat,
    getTimeFormat
} from '../../../../../Apis/functions';
import {
    EmailSubsectionChip,
    IconScheduleTable,
    PhoneLight,
    TimePickerChip,
} from '../../../../../components/buttons/IconButtons';
import CustomFileCard from '../../../../../components/CustomFileCard';
import CustomTooltip from '../../../../../components/CustomTooltip';
import NotInformation from '../../../../../components/NotInformation';
import { getFileIcon } from '../../../../../Helpers/fileType';
import { POSITION_MODEL } from '../../../../../Helpers/PositionModel';
/**
 * ClosedDeliveryCard Component
 *
 * This component renders a delivery information card, including user details,
 * contact information, date/time, and supporting documents.
 *
 * PARAMETERS:
 * @param {Object}   item - Data object containing delivery information.
 *                          If not provided, a “no_trail_information” message is displayed.
 * @param {Function} t - Translation function used to localize UI text.
 * @param {Function} IconComponent - Icon component displayed in the card header.
 * @param {String}   iconClass - Additional className applied to the icon.
 * @param {String}   iconCardClass - CSS class for the icon container background.
 * @param {String}   titleKey - Translation key for the main card title.
 * @param {Function} dispatch - Redux dispatcher used for downloading documents.
 * @param {String}   option - Option sent to the download handler.
 */
const ClosedDeliveryCard = ({
    item,
    t,
    IconComponent,
    iconClass = '',
    iconCardClass = 'background-secondary-100',
    titleKey = 'delivery_created_by',
}) => {
    if (!item) {
        return <NotInformation text={t('no_trail_information')} card={true} />;
    }
    return (
        <div className='row'>
            <div
                className={`p-8px border-radius-8px justify-content-center d-flex w-44px h-44px mt-4 ${iconCardClass}`}
            >
                <IconComponent className={iconClass} />
            </div>
            <div className='col'>
                <div className='row my-4 px-0'>
                    <p className='font-color-general-950 fs-14px ibl-font-w-600 px-0'>{t(titleKey)}</p>
                    <p className='font-color-general-950 fs-14px px-0 text-capitalize'>{item.closedByFullName}</p>
                    <div className='col-12 col-lg-6 px-0'>
                        <div className='d-flex align-items-center'>
                            <EmailSubsectionChip />
                            <CustomTooltip
                                text={item.closedByEmail}
                                classes={'font-color-general-950 fs-14px ml-2'}
                                showIfTruncated={true}
                                placement={'top'}
                            />
                        </div>
                    </div>
                    <div className='col-12 col-lg-6 px-0'>
                        <div className='d-flex align-items-center'>
                            <PhoneLight />
                            <p className='font-color-general-950 fs-14px ml-2'>{item.closedByPhoneNumber}</p>
                        </div>
                    </div>
                    <div className='border-bottom-shades-300 mt-3' />
                </div>
                <div className='row my-4 px-0'>
                    <p className='font-color-general-950 fs-14px ibl-font-w-600 px-0'>{t('date_and_time')}</p>
                    <div className='col-sm-12 col-md-6 px-0'>
                        <div className='d-flex align-items-center'>
                            <IconScheduleTable />
                            <p className='font-color-general-950 fs-14px ml-2'>
                                {getDateFormat(item.closedDate, false)}
                            </p>
                        </div>
                    </div>
                    <div className='col-sm-12 col-md-6 px-0'>
                        <div className='d-flex align-items-center'>
                            <TimePickerChip />
                            <p className='font-color-general-950 fs-14px ml-2'> {getTimeFormat(item.closedDate)}</p>
                        </div>
                    </div>
                    <div className='border-bottom-shades-300 mt-3' />
                </div>
                <div className='row my-4 px-0'>
                    <p className='font-color-general-950 fs-14px ibl-font-w-600 px-0'>{t('note')}</p>
                    <p className='font-color-general-950 fs-14px ibl-font-w-400 px-0 text-wrap'>{t(item.closedNote)|| EMPTY_VALUES.DEFAULT}</p>
                    <p className='font-color-general-950 fs-14px ibl-font-w-600 mt-3 mb-3 px-0'>{t('evidence')}</p>
                    {item.closedFile ? (
                        <CustomFileCard
                            downloadAtEnd={POSITION_MODEL.RIGHT}
                            showIcon
                            Icon={getFileIcon(item.closedFile?.userFileNames?.[0])}
                            title={item.closedFile?.userFileNames?.[0] || ''}
                            fileValue={item.closedFile?.filePath}
                            showText={false}
                            textField={item.closedFile?.path}
                            showBtnDownload
                            subtitle={`${t('uploaded')}: ${getDateFormat(item.closedDate, false, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)}`}
                            showChip={false}
                            buttonId={`btn_cdt_download_${item.id}`}
                            disabledBtnDownload={!item.closedFile}
                            onClick={() => {
                                downloadActionFile(URL + item?.closedFile?.filePath);
                            }}
                        />
                    )
                    : EMPTY_VALUES.DEFAULT}
                </div>
            </div>
        </div>
    );
};
export default ClosedDeliveryCard;
